import React, { useState } from 'react';
import { DownloadIcon, EyeIcon, PlusIcon, PrinterIcon, SearchIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Quotation, WalkInClient } from '../../types';
import { useData } from '../../store/DataContext';
import { BillingDocumentPreviewModal, DocumentPreviewAction } from './BillingDocumentPreviewModal';
import { BillingSection } from './BillingModule';
import { formatCurrency } from './utils';
interface Props {
  onNavigate: (section: BillingSection) => void;
}
export const Quotations: React.FC<Props> = ({
  onNavigate
}) => {
  const {
    quotations,
    patients,
    walkInClients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [previewAction, setPreviewAction] = useState<DocumentPreviewAction>('view');
  const openPreview = (quotation: Quotation, action: DocumentPreviewAction) => {
    setPreviewAction(action);
    setSelectedQuotation(quotation);
  };
  const filteredQuotations = quotations.filter(q => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (q.quotationNumber.toLowerCase().includes(query)) return true;
    let customerName = '';
    if (q.customerType === 'Patient') {
      const p = patients.find(p => p.id === q.customerId);
      if (p) customerName = `${p.firstName} ${p.lastName}`;
    } else {
      const c = walkInClients.find(c => c.id === q.customerId);
      if (c) customerName = c.name;
    }
    return customerName.toLowerCase().includes(query);
  });
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn') => {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    } else {
      const c = walkInClients.find(c => c.id === customerId);
      return c ? c.name : 'Unknown Client';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Accepted':
        return 'bg-green-100 text-green-800';
      case 'Declined':
        return 'bg-red-100 text-red-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Invoiced':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-1628" data-mp="gii8oupiem7u">
      <div className="flex justify-between items-center" data-id="element-1629" data-mp="lgtgz105oony">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1630" data-mp="lga0zpu2gack">Quotations</h1>
        <Button onClick={() => onNavigate('new-quotation' as any)} data-id="element-1631" data-mp="6sourn1w9v0f">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-1632" data-mp="br04lxs6w5in" /> New Quotation
        </Button>
      </div>

      <Card noPadding data-id="element-1633" data-mp="bonj5an83d0u">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1634" data-mp="zg2nta2h26f9">
          <div className="relative max-w-md" data-id="element-1635" data-mp="zeriv6xaismk">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1636" data-mp="147kri5vvql3x">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1637" data-mp="2owoev7upbwwj" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by quotation number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1638" data-mp="2jz51sb9qpq66" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1639" data-mp="2123w0o8d3m16">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1640" data-mp="2ezsddm380b1h">
            <thead className="bg-gray-50" data-id="element-1641" data-mp="2axyixvjmc39r">
              <tr data-id="element-1642" data-mp="2ax5kdhutck2p">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1643" data-mp="2awnd7q6m4lg0">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1644" data-mp="3mdviseszw7lr">
                  Quotation#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1645" data-mp="lk8wdcughfw8">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1646" data-mp="uht1wxzcpycn">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1647" data-mp="14h0az8z1z6ty">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1648" data-mp="18gizeeq3201j">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1649" data-mp="1i7dk3enokrkv">
              {filteredQuotations.length > 0 ? filteredQuotations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(quo => <tr key={quo.id} className="hover:bg-gray-50" data-id="element-1650" data-mp="37pkadfd66p6c">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1651" data-mp="37rdd4jknwp92">
                        {new Date(quo.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-1652" data-mp="2xsxnkl1a9o1o">
                        {quo.quotationNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1653" data-mp="3qgtiplb7f7wc">
                        {getCustomerName(quo.customerId, quo.customerType)}
                        <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-1654" data-mp="3gqify239b4r1">
                          ({quo.customerType})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1655" data-mp="d9icxfo7pka5">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quo.status)}`} data-id="element-1656" data-mp="d7fce1pfo8r6">
                          {quo.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1657" data-mp="1xr10vyk0vem3">
                        {formatCurrency(quo.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1658" data-mp="1opb7woahs7m7">
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500" title="View quotation" aria-label={`View quotation ${quo.quotationNumber}`} onClick={() => openPreview(quo, 'view')} data-id="element-1659" data-mp="1stizct8c41o4">
                          <EyeIcon className="h-4 w-4" data-id="element-1660" data-mp="12hxo0ed373pg" />
                        </button>
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Print quotation" aria-label={`Print quotation ${quo.quotationNumber}`} onClick={() => openPreview(quo, 'print')} data-id="element-1661" data-mp="xj4ailxsvcpa">
                          <PrinterIcon className="h-4 w-4" data-id="element-1662" data-mp="1g8kc7cl81b7a" />
                        </button>
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Download quotation PDF" aria-label={`Download quotation ${quo.quotationNumber} as PDF`} onClick={() => openPreview(quo, 'download')} data-id="element-1663" data-mp="1v74mxeggdvb8">
                          <DownloadIcon className="h-4 w-4" data-id="element-1664" data-mp="3644rh6nks5q3" />
                        </button>
                      </td>
                    </tr>) : <tr data-id="element-1665" data-mp="3fwk8o1r1sq9x">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-1666" data-mp="3kuvl8k8q9evu">
                    No quotations found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedQuotation && <BillingDocumentPreviewModal record={selectedQuotation} documentType="Quotation" customer={getQuotationCustomer(selectedQuotation, patients, walkInClients)} initialAction={previewAction} onClose={() => setSelectedQuotation(null)} data-id="element-1667" data-mp="2x516ty8rfi9h" />}
    </div>;
};
function getQuotationCustomer(quotation: Quotation, patients: Patient[], walkInClients: WalkInClient[]) {
  if (quotation.customerType === 'Patient') {
    const patient = patients.find(entry => entry.id === quotation.customerId);
    return {
      name: patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient',
      fileNumber: patient?.bmcFileNumber,
      address: patient?.address,
      contact: patient?.contact
    };
  }
  const walkInClient = walkInClients.find(entry => entry.id === quotation.customerId);
  return {
    name: walkInClient?.name || 'Walk-in client',
    address: walkInClient?.address,
    contact: walkInClient?.phone
  };
}